/**
 * Mordeczka.eu Admin - Login Page
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Card, Typography, Alert, Space } from "antd";
import { LockOutlined, MailOutlined, BookOutlined } from "@ant-design/icons";
import { useAuthStore } from "../stores/authStore";

const { Title, Text } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const { login, isLoading, error, clearError, isAuthenticated } =
    useAuthStore();
  const [loginError, setLoginError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as { from?: string })?.from || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors on mount
  useEffect(() => {
    clearError();
    setLoginError(null);
  }, [clearError]);

  const handleSubmit = async (values: LoginFormValues) => {
    setLoginError(null);
    const success = await login(values.email, values.password);
    if (success) {
      const from = (location.state as { from?: string })?.from || "/";
      navigate(from, { replace: true });
    } else {
      setLoginError(error || "Wystąpił błąd podczas logowania");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
        padding: 24,
      }}
    >
      <Card
        style={{
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 20px 60px rgba(33, 150, 243, 0.15)",
          borderRadius: 16,
          border: "none",
        }}
        bodyStyle={{ padding: "40px 32px" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Logo & Title */}
          <div style={{ textAlign: "center" }}>
            <BookOutlined
              style={{ fontSize: 48, color: "#1976d2", marginBottom: 16 }}
            />
            <Title level={2} style={{ margin: 0, color: "#1565c0" }}>
              Mordeczka.eu
            </Title>
            <Text type="secondary">Panel administracyjny</Text>
          </div>

          {/* Error Alert */}
          {(loginError || error) && (
            <Alert
              title={loginError || error}
              type="error"
              showIcon
              closable
              onClose={() => {
                setLoginError(null);
                clearError();
              }}
            />
          )}

          {/* Login Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Podaj adres email" },
                { type: "email", message: "Nieprawidłowy format email" },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: "#90caf9" }} />}
                placeholder="Email"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: "Podaj hasło" }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#90caf9" }} />}
                placeholder="Hasło"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                style={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  background:
                    "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
                  border: "none",
                  borderRadius: 8,
                }}
              >
                {isLoading ? "Logowanie..." : "Zaloguj się"}
              </Button>
            </Form.Item>
          </Form>

          {/* Footer */}
          <div style={{ textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              © 2026 Mordeczka.eu - Moja biblioteczka
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}

export default LoginPage;
